import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Close,
} from '@mui/icons-material';
import { getAssessmentQuestions } from '../../utils/api';
import ChangeLanguage from '../sections/ChangeLanguage';

function Questions() {
  const { assessmentId } = useParams();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const navigate = useNavigate();
  
  // Helper function to get question text based on current language
  const getQuestionText = (question) => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' || currentLang === 'zh-TW' || currentLang === 'zh-CN') {
      return question.questionZh || question.question;
    }
    return question.questionEn || question.question;
  };
  
  // Helper function to get option description based on current language
  const getOptionDescription = (option) => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' || currentLang === 'zh-TW' || currentLang === 'zh-CN') {
      return option.descriptionZh || option.description;
    }
    return option.descriptionEn || option.description;
  };
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionsData, setQuestionsData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  
  // Timer states
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionTimes, setQuestionTimes] = useState({}); // {questionIndex: timeInSeconds}

  useEffect(() => {
    fetchQuestions();
  }, [assessmentId]);

  // Start timer when questions are loaded
  useEffect(() => {
    if (questionsData && questionStartTime === null) {
      setQuestionStartTime(Date.now());
    }
  }, [questionsData]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getAssessmentQuestions(assessmentId);
      
      // DEBUG: Log API response to verify bilingual fields
      if (data && data.questions && data.questions.length > 0) {
        console.log('=== API Response Sample ===');
        console.log('First question:', data.questions[0].question);
        console.log('First option:', data.questions[0].options?.[0]);
        console.log('==========================');
      }
      
      // Sort options by score field for each question (same as LEVAVAPP)
      // This ensures options are displayed in ascending order: lowest to highest score
      if (data && data.questions) {
        data.questions.forEach((questionItem, qIndex) => {
          if (questionItem.options && questionItem.options.length > 0) {
            // Sort by score (ascending order: lowest to highest)
            // Same logic as LEVAVAPP: Questions.dart line 1779-1780
            questionItem.options.sort((a, b) => {
              const scoreA = a.score !== undefined && a.score !== null ? a.score : 0;
              const scoreB = b.score !== undefined && b.score !== null ? b.score : 0;
              return scoreA - scoreB;
            });
          }
        });
      }
      
      setQuestionsData(data);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError(t('Questions.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const stopQuestionTimer = () => {
    if (!questionStartTime) return 0;
    return Math.floor((Date.now() - questionStartTime) / 1000); // seconds
  };

  const handleAnswerSelect = (optionId) => {
    const currentQuestion = questionsData.questions[currentQuestionIndex];
    setAnswers({
      ...answers,
      [currentQuestionIndex]: {
        questionId: currentQuestion.question.id,
        optionId: optionId,
      },
    });
  };

  const handleNext = () => {
    // Record time spent on current question
    const timeSpent = stopQuestionTimer();
    setQuestionTimes(prev => ({
      ...prev,
      [currentQuestionIndex]: (prev[currentQuestionIndex] || 0) + timeSpent
    }));
    
    if (currentQuestionIndex < questionsData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setQuestionStartTime(Date.now()); // Start timer for next question
    }
  };

  const handlePrevious = () => {
    // Record time spent on current question (accumulate)
    const timeSpent = stopQuestionTimer();
    setQuestionTimes(prev => ({
      ...prev,
      [currentQuestionIndex]: (prev[currentQuestionIndex] || 0) + timeSpent
    }));
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setQuestionStartTime(Date.now()); // Start timer for previous question
    }
  };

  const handleSubmit = () => {
    // Record time spent on last question
    const timeSpent = stopQuestionTimer();
    const finalTimes = {
      ...questionTimes,
      [currentQuestionIndex]: (questionTimes[currentQuestionIndex] || 0) + timeSpent
    };
    
    // Calculate total time
    const totalTime = Object.values(finalTimes).reduce((sum, time) => sum + time, 0);
    
    // Convert answers object to array format with time data
    const answersArray = Object.keys(answers).map(index => ({
      ...answers[index],
      timeSpent: finalTimes[index] || 0, // Add time spent for each question
    }));
    
    // Navigate to result page with answers and timing data
    navigate(`/student/assessment/${assessmentId}/result${taskId ? `?taskId=${taskId}` : ''}`, {
      state: { 
        answers: answersArray,
        questionsData: questionsData,
        totalTime: totalTime, // Add total time
        questionTimes: finalTimes, // Add individual question times
      },
    });
  };

  const handleClose = () => {
    if (window.confirm(t('Questions.exitConfirm'))) {
      navigate('/student-home');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('Questions.loadingQuestions')}</Typography>
      </Container>
    );
  }

  if (error || !questionsData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('Questions.questionsNotFound')}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/student-home')}>
          {t('AssessmentDetail.backToHome')}
        </Button>
      </Container>
    );
  }

  const currentQuestion = questionsData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questionsData.questions.length) * 100;
  const isLastQuestion = currentQuestionIndex === questionsData.questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;
  const currentAnswer = answers[currentQuestionIndex]?.optionId;
  const isAnswered = currentAnswer !== undefined; // Check if current question is answered

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('Questions.questionOf', { current: currentQuestionIndex + 1, total: questionsData.questions.length })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ChangeLanguage buttonColor="#4e7ba8" />
            <IconButton onClick={handleClose} sx={{ color: '#d32f2f' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
            {Object.keys(answers).length} {t('Questions.of')} {questionsData.questions.length} {t('Questions.answered')}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Question Card */}
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
          {/* Section Type */}
          {currentQuestion.question.sectionType && (
            <Typography
              variant="body2"
              sx={{
                color: '#4e7ba8',
                fontWeight: 600,
                display: 'block',
                mb: 2,
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}
            >
              {currentQuestion.question.sectionType === 'PSS' 
                ? t('Questions.sectionPSS')
                : currentQuestion.question.sectionType === 'SCC'
                ? t('Questions.sectionSCC')
                : currentQuestion.question.sectionType === 'FA'
                ? t('Questions.sectionFA')
                : currentQuestion.question.sectionType
              }
            </Typography>
          )}

          {/* Question Text */}
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            {getQuestionText(currentQuestion.question)}
          </Typography>

          {/* Options */}
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={currentAnswer !== undefined ? currentAnswer : ''}
              onChange={(e) => handleAnswerSelect(parseInt(e.target.value))}
            >
              {currentQuestion.options && currentQuestion.options.length > 0 ? (
                currentQuestion.options.map((option) => (
                  <FormControlLabel
                    key={option.optionId}
                    value={option.optionId}
                    control={<Radio sx={{ color: '#4e7ba8', '&.Mui-checked': { color: '#4e7ba8' } }} />}
                    label={getOptionDescription(option)}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      mb: 2,
                      py: 1.5,
                      px: 2,
                      ml: 0,
                      mr: 0,
                      transition: 'all 0.2s',
                      bgcolor: currentAnswer === option.optionId ? 'rgba(78, 123, 168, 0.08)' : 'transparent',
                      borderColor: currentAnswer === option.optionId ? '#4e7ba8' : '#e0e0e0',
                      '&:hover': {
                        bgcolor: 'rgba(78, 123, 168, 0.04)',
                        borderColor: '#4e7ba8',
                      },
                    }}
                  />
                ))
              ) : (
                <Alert severity="warning">{t('Questions.noOptionsAvailable')}</Alert>
              )}
            </RadioGroup>
          </FormControl>
        </Paper>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={isFirstQuestion}
            startIcon={<ArrowBack />}
            sx={{ flex: 1 }}
          >
            {t('Questions.previous')}
          </Button>
          
          {isLastQuestion ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isAnswered}
              sx={{
                flex: 1,
                bgcolor: isAnswered ? '#4CAF50' : '#BDBDBD',
                '&:hover': { 
                  bgcolor: isAnswered ? '#45a049' : '#BDBDBD',
                },
                '&.Mui-disabled': {
                  bgcolor: '#BDBDBD',
                  color: 'rgba(0, 0, 0, 0.38)',
                },
              }}
            >
              {t('Questions.submit')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isAnswered}
              endIcon={<ArrowForward />}
              sx={{
                flex: 1,
                bgcolor: isAnswered ? '#4e7ba8' : '#BDBDBD',
                '&:hover': { 
                  bgcolor: isAnswered ? '#3d5f85' : '#BDBDBD',
                },
                '&.Mui-disabled': {
                  bgcolor: '#BDBDBD',
                  color: 'rgba(0, 0, 0, 0.38)',
                },
              }}
            >
              {t('Questions.next')}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default Questions;
